import * as React from 'react';
import ReactSVG from 'react-inlinesvg';

import { useTheme } from '@emotion/react';
import type { Placement } from '@floating-ui/core';

import { Theme, styled } from '../theme';
import { Size, matchSize } from '../utility/common-props-helpers';
import Tooltip from './Tooltip';

const getDimension = matchSize({
    md: 12,
    lg: 16,
    xl: 24,
    '2xl': 50,
    '3xl': 115,
});

const StyledSvgIcon = styled('div')<{
    size: Size;
    width?: string | number;
    height?: string | number;
    color?: string;
}>(({ size, width, height, color }) => ({
    display: 'inline-block',
    verticalAlign: 'middle',
    cursor: 'pointer',
    width: width != null ? width : getDimension(size),
    height: height != null ? height : getDimension(size),
    color: color || 'inherit',
    svg: {
        display: 'block',
        fill: 'currentColor',
        color: color || 'inherit',
        width: width != null ? width : getDimension(size),
        height: height != null ? height : getDimension(size),
    },
}));

export type SvgIconProps = {
    className?: string;
    style?: React.CSSProperties;
    size?: Size;
    width?: number | string;
    height?: number | string;
    src: string;
    alt?: string;
    onClick?: (ev) => void;
    onMouseDown?: (ev) => void;
    color?: (theme: Theme) => string;
    tooltip?: string;
    tooltipPlacement?: Placement;
    disabled?: boolean;
};

/**
 * Component
 *
 * @example
 * ```
 * <SvgIcon alt="github" src="/icons/ic-github.svg" color={theme => theme.color.green1} />
 * <SvgIcon alt="github" src={require('/icons/ic-github.svg')} />
 * ```
 */
const SvgIcon = React.forwardRef(
    (
        {
            src,
            alt,
            size = 'md',
            color,
            tooltip,
            tooltipPlacement,
            disabled,
            onClick,
            ...props
        }: SvgIconProps,
        ref
    ) => {
        const theme = useTheme();
        const handleClick = (ev) => {
            onClick?.(ev);
        };

        const icon = (
            <StyledSvgIcon
                size={size}
                ref={ref}
                color={color && color(theme)}
                onClick={handleClick}
                {...(alt ? { role: 'img', 'aria-label': alt } : {})}
                {...props}
            >
                {/* `title={null}` strips any <title> baked into the asset; the browser
                    renders it as a native tooltip that duplicates our own Tooltip. */}
                <ReactSVG src={src} style={{ display: 'flex' }} cacheRequests={true} title={null} />
            </StyledSvgIcon>
        );

        const withTooltip = !!tooltip && !disabled;

        if (!withTooltip) {
            return icon;
        }

        return (
            <Tooltip title={tooltip} placement={tooltipPlacement}>
                {icon}
            </Tooltip>
        );
    }
);

export default SvgIcon;
